/**
 * Custom Step Executor — Extensible custom handler lookup
 *
 * Config:
 * - handlerName: string — registered custom handler name
 * - params?: Record<string, unknown> — parameters passed to the handler
 *
 * Register handlers via registerCustomHandler() before use.
 */

import type { EngineStep, EngineExecutionContext, StepExecutionResult, StepExecutor } from '../types';

export type CustomStepHandler = (
  params: Record<string, unknown>,
  context: EngineExecutionContext
) => Promise<StepExecutionResult>;

/** Registry of named custom handlers */
const customHandlers: Map<string, CustomStepHandler> = new Map();

/**
 * Register a custom step handler
 */
export function registerCustomHandler(name: string, handler: CustomStepHandler): void {
  customHandlers.set(name, handler);
}

/**
 * Unregister a custom step handler
 */
export function unregisterCustomHandler(name: string): void {
  customHandlers.delete(name);
}

/**
 * Get all registered custom handler names
 */
export function getCustomHandlerNames(): string[] {
  return Array.from(customHandlers.keys());
}

export const customStepExecutor: StepExecutor = {
  type: 'custom',
  async execute(step: EngineStep, context: EngineExecutionContext): Promise<StepExecutionResult> {
    try {
      const handlerName = step.config.handlerName as string;
      if (!handlerName) {
        return { success: false, error: 'handlerName is required for custom step' };
      }

      const handler = customHandlers.get(handlerName);
      if (!handler) {
        return { success: false, error: `Custom handler not found: "${handlerName}"` };
      }

      const params = (step.config.params as Record<string, unknown>) || {};
      return await handler(params, context);
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Custom step failed',
      };
    }
  },
};
